import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import { clearUploadProgress, setUploadProgress } from '@/store/slices/poc/heliaSlice';
import { finishCloning } from '@/store/slices/poc/cloneQueueSlice';
import { findUploadEntryByCid } from '@/store/utils/uploadProgress.utils';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import { addFileToHeliaThunk } from '@/store/thunks/poc/helia';
import {
  isIncomingMessageOfType,
  type IncomingMessageOfType,
} from '@/store/listeners/poc/incomingMessageMatchers';
import {
  armInitialReplicationWatchdog,
  handleReplicationFailed,
  handleReplicationProgress,
  handleReplicationStarted,
  stopAllReplicationTracking,
  stopReplicationTrackingByCid,
  stopReplicationTrackingByUploadId,
} from '@/store/listeners/poc/uploadReplicationCoordinator';
import type { AppStartListening } from '@/store/listeners/types';
import { createLogger } from '@/utils/logger';

const log = createLogger({ namespace: 'six3ro:p2p:listeners:replication' });

const COMPLETE_PROGRESS = 100;
const HANDOFF_PROGRESS = 80;

export function registerIncomingReplicationListeners(
  startAppListening: AppStartListening
): void {
  // Backend handoff succeeded — arm the silence watchdog. Completion now depends
  // on the server peer cloning the file (fileReplicationProgress → uploadSuccessful);
  // if nothing arrives within the silence window, the upload errors instead of
  // hanging at 'announcing'.
  startAppListening({
    actionCreator: addFileToHeliaThunk.fulfilled,
    effect: (action, listenerApi) => {
      const { uploadId, metadata } = action.payload;
      armInitialReplicationWatchdog(listenerApi, {
        uploadId,
        fileName: metadata.name,
        cid: metadata.cid,
        progress: HANDOFF_PROGRESS,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'fileReplicationStarted'>> =>
      isIncomingMessageOfType(action, 'fileReplicationStarted'),
    effect: (action, listenerApi) => {
      handleReplicationStarted(listenerApi, {
        cid: action.payload.result.data.cid,
        totalBytes: action.payload.result.data.totalBytes,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'fileReplicationProgress'>> =>
      isIncomingMessageOfType(action, 'fileReplicationProgress'),
    effect: (action, listenerApi) => {
      handleReplicationProgress(listenerApi, {
        cid: action.payload.result.data.cid,
        stage: action.payload.result.data.stage,
        bytesTransferred: action.payload.result.data.bytesTransferred,
        totalBytes: action.payload.result.data.totalBytes,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'fileReplicationFailed'>> =>
      isIncomingMessageOfType(action, 'fileReplicationFailed'),
    effect: (action, listenerApi) => {
      handleReplicationFailed(listenerApi, {
        cid: action.payload.result.data.cid,
        reason: action.payload.result.data.reason,
      });
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'uploadSuccessful'>> =>
      isIncomingMessageOfType(action, 'uploadSuccessful'),
    effect: (action, listenerApi) => {
      const { cid } = action.payload.result.data;
      log.info(`uploadSuccessful listener fired for cid: ${cid}`);

      stopReplicationTrackingByCid(listenerApi, cid);

      const currentClone = listenerApi.getState().cloneQueue.currentlyCloning;
      if (currentClone?.cid === cid) {
        listenerApi.dispatch(finishCloning());
      }

      // HAP confirmed the upload is available. Mark the matching upload entry
      // complete. The message carries no uploadId, so the entry is resolved by
      // CID (set on the entry during the announcing stage, before handoff).
      const uploadProgress = listenerApi.getState().helia.uploadProgress;
      const uploadEntry = findUploadEntryByCid(uploadProgress, cid);
      if (uploadEntry === undefined) {
        const storedCids = Object.values(uploadProgress).map((e) => e.cid);
        log.warn(`uploadSuccessful: no upload entry found for cid ${cid}. stored cids: [${storedCids.join(', ')}]`);
        return;
      }

      log.info(`uploadSuccessful: marking upload ${uploadEntry.uploadId} complete`);
      listenerApi.dispatch(setUploadProgress({
        uploadId: uploadEntry.uploadId,
        fileName: uploadEntry.fileName,
        cid,
        stage: 'complete',
        progress: COMPLETE_PROGRESS,
      }));
    },
  });

  startAppListening({
    actionCreator: clearUploadProgress,
    effect: (action) => {
      stopReplicationTrackingByUploadId(action.payload);
    },
  });

  startAppListening({
    actionCreator: logoutThunk.pending,
    effect: () => {
      stopAllReplicationTracking();
    },
  });
}
